// Helper functions for fetching data from Sanity CMS
import { slugifyGeographyName } from 'alakai-aloha-shared';
import { sanityFetch } from './sanity';
import { regionQueries, areaQueries, contentQueries, homebaseResultQueries } from './sanityQueries';
import { normalizeActivityDocuments } from './activityTaxonomy';

// Region data fetchers
export const fetchRegions = async () => {
  try {
    return await sanityFetch(regionQueries.getAllRegions);
  } catch (error) {
    console.error('Error fetching regions:', error);
    return [];
  }
};

export const fetchRegionBySlug = async (slug) => {
  try {
    return await sanityFetch(regionQueries.getRegionBySlug, { slug });
  } catch (error) {
    console.error('Error fetching region:', error);
    return null;
  }
};

export const fetchRegionsWithAreas = async () => {
  try {
    return await sanityFetch(regionQueries.getRegionsWithAreas);
  } catch (error) {
    console.error('Error fetching regions with areas:', error);
    return [];
  }
};

// Area data fetchers
export const fetchAreas = async () => {
  try {
    return await sanityFetch(areaQueries.getAllAreas);
  } catch (error) {
    console.error('Error fetching areas:', error);
    return [];
  }
};

export const fetchAreaBySlug = async (slug) => {
  try {
    return await sanityFetch(areaQueries.getAreaBySlug, { slug });
  } catch (error) {
    console.error('Error fetching area:', error);
    return null;
  }
};

export const fetchAreasByRegion = async (regionSlug) => {
  try {
    return await sanityFetch(areaQueries.getAreasByRegion, { regionSlug });
  } catch (error) {
    console.error('Error fetching areas by region:', error);
    return [];
  }
};

// Content fetchers with pagination
export const fetchActivitiesByRegion = async (regionSlug, limit = 6) => {
  try {
    return normalizeActivityDocuments(
      await sanityFetch(contentQueries.getActivitiesByRegion, { regionSlug, limit })
    );
  } catch (error) {
    console.error('Error fetching activities by region:', error);
    return [];
  }
};

export const fetchActivitiesByArea = async (areaSlug, limit = 6) => {
  try {
    return normalizeActivityDocuments(
      await sanityFetch(contentQueries.getActivitiesByArea, { areaSlug, limit })
    );
  } catch (error) {
    console.error('Error fetching activities by area:', error);
    return [];
  }
};

export const fetchRestaurantsByRegion = async (regionSlug, limit = 6) => {
  try {
    return await sanityFetch(contentQueries.getRestaurantsByRegion, { regionSlug, limit });
  } catch (error) {
    console.error('Error fetching restaurants by region:', error);
    return [];
  }
};

export const fetchRestaurantsByArea = async (areaSlug, limit = 6) => {
  try {
    return await sanityFetch(contentQueries.getRestaurantsByArea, { areaSlug, limit });
  } catch (error) {
    console.error('Error fetching restaurants by area:', error);
    return [];
  }
};

export const fetchLodgingByRegion = async (regionSlug, limit = 6) => {
  try {
    return await sanityFetch(contentQueries.getLodgingByRegion, { regionSlug, limit });
  } catch (error) {
    console.error('Error fetching lodging by region:', error);
    return [];
  }
};

export const fetchLodgingByArea = async (areaSlug, limit = 6) => {
  try {
    return await sanityFetch(contentQueries.getLodgingByArea, { areaSlug, limit });
  } catch (error) {
    console.error('Error fetching lodging by area:', error);
    return [];
  }
};

export const fetchBlogPostsByRegion = async (regionSlug, limit = 3) => {
  try {
    return await sanityFetch(contentQueries.getBlogPostsByRegion, { regionSlug, limit });
  } catch (error) {
    console.error('Error fetching blog posts by region:', error);
    return [];
  }
};

export const fetchBlogPostsByArea = async (areaSlug, limit = 3) => {
  try {
    return await sanityFetch(contentQueries.getBlogPostsByArea, { areaSlug, limit });
  } catch (error) {
    console.error('Error fetching blog posts by area:', error);
    return [];
  }
};

export const fetchHomebaseResultSupport = async ({ areaSlug, regionSlug }) => {
  try {
    if (!areaSlug || !regionSlug) {
      return { guides: [], blogPosts: [] };
    }
    const result = await sanityFetch(homebaseResultQueries.getSupportContent, { areaSlug, regionSlug });
    return {
      guides: result?.guides || [],
      blogPosts: result?.blogPosts || [],
    };
  } catch (error) {
    console.error('Error fetching homebase result support:', error);
    return { guides: [], blogPosts: [] };
  }
};

// Utility function to generate region slug from name
export const generateRegionSlug = (regionName) => {
  return slugifyGeographyName(regionName);
};

// Utility function to generate area slug from name
export const generateAreaSlug = (areaName) => {
  return slugifyGeographyName(areaName);
};
